import { LANGUAGE_CATALOG, tt } from '@agos/shared'

/**
 * A franchise's third-party credentials, as the Control Tower is allowed to see them.
 *
 * **The values never leave the server.** Every secret arrives as `{ "hasValue": true }` — a presence
 * flag and nothing else. Measured, all nine of them:
 *
 * ```
 * fiskal.restauracia.apiKey      true    integrations.woltApiKey        false
 * fiskal.rozvoz.apiKey           false   integrations.boltApiKey        false
 * payments.tatrapayClientSecret  true    integrations.googleMapsApiKey  false
 * email.resendApiKey             true    integrations.anthropicApiKey   true
 * sms.bulkgateAppToken           true
 * ```
 *
 * That is the right shape and this file exists to keep it. Three of the findings in this repository —
 * FB-132, FB-138 and FB-143 — are endpoints that handed a live key to a browser; this one does not, and
 * nothing here asks it to.
 *
 * **A blank field means *unchanged*, never *clear*.** The client cannot send a secret back because it
 * never received one, so an empty box on save must be omitted from the payload rather than sent as `""`.
 * The same trap as the masked `databaseUrl`: round-tripping what you were shown replaces the value with
 * the mask.
 */

export interface SecretState {
  /** Dotted path, e.g. `email.resendApiKey`. */
  path: string
  configured: boolean
}

export interface IntegrationsView {
  groups: string[]
  secrets: SecretState[]
}

function isSecret(value: unknown): value is { hasValue: unknown } {
  if (typeof value !== 'object' || value === null) return false
  const keys = Object.keys(value)
  return keys.length === 1 && keys[0] === 'hasValue'
}

/** Every masked secret in the payload, wherever it is nested. */
export function integrationsOf(payload: unknown): IntegrationsView {
  const secrets: SecretState[] = []

  function walk(value: unknown, path: string): void {
    if (isSecret(value)) {
      secrets.push({ path, configured: value.hasValue === true })
      return
    }
    if (typeof value !== 'object' || value === null || Array.isArray(value)) return
    for (const [key, child] of Object.entries(value)) {
      walk(child, path === '' ? key : `${path}.${key}`)
    }
  }

  walk(payload, '')
  const row = (typeof payload === 'object' && payload !== null ? payload : {}) as Record<string, unknown>
  return { groups: Object.keys(row).sort(), secrets }
}

/** One setting the screen can display: a scalar the endpoint answered. */
export interface VisibleSetting {
  /**
   * The section it came from - the top-level key, or {@link ROOT_GROUP} for a scalar answered at the
   * top level rather than inside a section.
   */
  group: string
  /** Dotted path, e.g. `payments.callbackUrl`. */
  path: string
  /** The field's own name, for a label. */
  key: string
  value: string | number | boolean | null
}

/**
 * Every scalar the endpoint answers - §3.57, FB-642.
 *
 * **§3.53 measured the gap**: the service answers 48 keys and the screen showed 25, with **23 having
 * no mention anywhere in the Tower** - including `callbackUrl`, `returnUrl`, `testMode` and
 * `tatrapayClientId`, which are the subjects of §10A.143 and §10A.144. An operator debugging a
 * payment could not see which of those a franchise was wired to.
 *
 * **Generic on purpose.** Naming the 23 would close the count today and drift on the next key the
 * service gains - exactly how §10A.157's hand-named `published()` shipped having forgotten a column
 * a migration added. This walks the response, so the screen cannot fall behind the endpoint.
 *
 * **A secret can never appear here, and not because of a list.** Credentials arrive as
 * `{hasValue: boolean}` and {@link isSecret} recognises that *shape*, so they are left to the
 * toggles. `passwordHash` and `keyValue` - the two of §3.53's 23 that must never be drawn - are
 * excluded by construction rather than by being remembered.
 *
 * Objects are descended into one level and arrays are joined, because the response nests by section
 * (`payments.*`, `sms.*`) and `autoTranslateLanguages` is a list of codes.
 */
export const ROOT_GROUP = 'general'

export function visibleSettingsOf(payload: unknown): VisibleSetting[] {
  const found: VisibleSetting[] = []

  function scalar(value: unknown): string | number | boolean | null | undefined {
    if (value === null) return null
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      return value
    }
    // A list of codes reads as one value; anything else is not a scalar.
    if (Array.isArray(value) && value.every((item) => typeof item === 'string')) {
      return value.join(', ')
    }
    return undefined
  }

  function walk(value: unknown, group: string, path: string): void {
    if (isSecret(value)) return
    const flat = scalar(value)
    if (flat !== undefined) {
      if (path !== '') {
        // **A scalar answered at the top level has no section**, and its own key is not one: taking
        // the key as the group gave every such field a heading of its own, one row long. They share
        // `ROOT_GROUP` instead. The first version of this got it wrong in code *and* in its own
        // javadoc, and no test covered the case - which is why there is one now.
        found.push({
          group: path.includes('.') ? group : ROOT_GROUP,
          path,
          key: path.split('.').pop() ?? path,
          value: flat,
        })
      }
      return
    }
    if (typeof value !== 'object' || value === null || Array.isArray(value)) return
    for (const [key, child] of Object.entries(value)) {
      const next = path === '' ? key : `${path}.${key}`
      walk(child, path === '' ? key : group, next)
    }
  }

  walk(payload, '', '')
  return found.sort((left, right) => left.path.localeCompare(right.path))
}

/** The sections, in the order the screen should draw them. */
export function settingGroupsOf(settings: readonly VisibleSetting[]): string[] {
  return [...new Set(settings.map((setting) => setting.group))].sort()
}

/**
 * How a value reads on screen.
 *
 * A boolean is the common case and the one a raw `true` communicates worst - `testMode` deciding
 * whether real cards are charged is the example §10A.143 is about. An empty string is *set to
 * nothing*, which is a different fact from absent.
 */
export function settingValueText(value: string | number | boolean | null): string {
  if (value === null) return tt('nenastavené')
  if (value === true) return tt('áno')
  if (value === false) return tt('nie')
  if (value === '') return tt('prázdne')
  // §3.190 - GPE's public key is answered whole, and thirty lines of base64 in a two-column list read as
  // nothing; which block it is (a certificate, a public key) is what somebody checking it needs.
  if (typeof value === 'string') {
    const block = /-----BEGIN ([A-Z0-9 ]+)-----/.exec(value)
    if (block?.[1] !== undefined) return `PEM: ${block[1]}`
  }
  return String(value)
}

/** The API key's dotted path, named once so the input, the note and the patch cannot disagree. */
export const TATRAPAY_CLIENT_ID = 'payments.tatrapayClientId'

/**
 * Whether this franchise has a TatraPay client id, and what to say about it - `SAAS.md` §3.84.
 *
 * **The owner asked how to fix CardPay for Šurany and the honest answer was *you cannot from here*.**
 * CardPay needs two values, `clientId` and `clientSecret`. The secret is a masked `{hasValue}` field
 * and has always had an input; the client id is answered **in the clear**, so `integrationsOf` -
 * which collects only `{hasValue}`-shaped entries - never saw it, and the only `<input>` on the
 * screen is inside that loop. `TenantIntegrationsService.PAYMENT_COLUMNS` accepts
 * `tatrapayClientId`, and `integrationPatches` would have routed it correctly. There was simply
 * nowhere to type it.
 *
 * §3.57 deferred this deliberately - *"twenty inputs need twenty validation decisions"* - and that
 * was right. This is one of the twenty, asked for by name, with one validation decision: a trimmed
 * non-empty string, which is what every other field on this form already does.
 */
export function clientIdNote(settings: readonly VisibleSetting[]): string {
  return clientIdIsSet(settings) ? tt('Nastavené') : tt('Nenastavené')
}

export function clientIdIsSet(settings: readonly VisibleSetting[]): boolean {
  const one = settings.find((setting) => setting.path === TATRAPAY_CLIENT_ID)
  if (one === undefined) return false
  return one.value !== null && one.value !== '' && one.value !== false
}

export function configuredCount(view: IntegrationsView): number {
  return view.secrets.filter((secret) => secret.configured).length
}

export function secretNote(secret: SecretState): string {
  return secret.configured ? tt('Nastavené') : tt('Nenastavené')
}

/**
 * What a save should carry.
 *
 * A field left blank is **omitted**, not sent as an empty string: the browser was never given the
 * current value, so a blank box means *leave it alone*. Sending `""` would erase a working credential,
 * and the operator would have no way to put it back because they cannot read it either.
 */
export function secretPayload(typed: Record<string, string>): Record<string, string> {
  const payload: Record<string, string> = {}
  for (const [path, value] of Object.entries(typed)) {
    if (value.trim() === '') continue
    payload[path] = value.trim()
  }
  return payload
}

/** Said above the form, because *blank means unchanged* is not obvious from a blank box. */
export function blankFieldNote(): string {
  return tt('Prázdne pole necháva pôvodnú hodnotu. Kľúče sa nedajú zobraziť, len prepísať.')
}

/**
 * Whether anything the operator typed looks like a mask rather than a key.
 *
 * Nobody should be pasting `***` or `hasValue` into a credential field, and if they do it is because
 * they copied it off a screen that showed one — which is exactly the mistake this shape prevents.
 */
export function looksLikeMask(value: string): boolean {
  const trimmed = value.trim()
  return trimmed !== '' && (/^\*+$/.test(trimmed) || trimmed === 'hasValue' || trimmed === '[object Object]')
}

export function maskWarning(typed: Record<string, string>): string {
  const suspect = Object.entries(typed).filter(([, value]) => looksLikeMask(value))
  if (suspect.length === 0) return ''
  return `${tt('Toto nevyzerá ako kľúč')}: ${suspect.map(([path]) => path).join(', ')}`
}

/**
 * The flat map turned into the bodies `PATCH …/integrations` actually takes - SAAS.md §3.5.
 *
 * **This is the part that was missing, and it is not a formality.** `secretPayload` above answers
 * *what changed*, keyed by the dotted path the read reports (`email.resendApiKey`). The endpoint
 * takes **nested sections** - `{email: {resendApiKey: "..."}}` - and `fiskal` is special: it carries a
 * `kasaId`, which `update` removes from the patch and uses to pick the row. So
 * `fiskal.restauracia.apiKey` is not a two-level path with a dot in it; it names a **register**.
 *
 * Which means one save can need **more than one request**: two registers are two rows, and the
 * endpoint writes one per call. Returned as a list for that reason rather than as one object, and the
 * caller sends them in sequence so a failure on the second does not leave the first unreported.
 *
 * A path this does not recognise is dropped rather than guessed at - the four section names are what
 * `update` branches on, and an unknown fifth would be accepted with a 200 and write nothing.
 */
/**
 * The blocks `TenantIntegrationsService.update` branches on, besides `fiskal`'s per-register rows.
 *
 * **FB-806 - one list, read twice.** The filter below dropped everything else, which is right, and
 * `integrationSectionsOf` drew every block the *read* returns as editable, which was not: the
 * `hikvision` block arrived with six inputs whose values this function threw away before the request.
 * So the same constant now decides what may be typed into, and
 * `EveryUnwritableBlockIsReadOnlyTest` holds the two together.
 */
export const WRITABLE_BLOCKS: readonly string[] = ['payments', 'email', 'sms', 'integrations']

export function integrationPatches(typed: Record<string, string>): Record<string, unknown>[] {
  const changed = secretPayload(typed)
  const sections: Record<string, Record<string, string>> = {}
  const registers: Record<string, Record<string, string>> = {}

  for (const [path, value] of Object.entries(changed)) {
    const parts = path.split('.')
    if (parts[0] === 'fiskal' && parts.length === 3) {
      const kasaId = parts[1] ?? ''
      const field = parts[2] ?? ''
      if (kasaId === '' || field === '') continue
      registers[kasaId] = { ...(registers[kasaId] ?? {}), [field]: value }
      continue
    }
    if (parts.length !== 2) continue
    const section = parts[0] ?? ''
    const field = parts[1] ?? ''
    // FB-806 - the same list the section builder reads, so a block this cannot carry is never drawn
    // as though it could be typed into.
    if (!WRITABLE_BLOCKS.includes(section) || field === '') continue
    sections[section] = { ...(sections[section] ?? {}), [field]: value }
  }

  const bodies: Record<string, unknown>[] = []
  for (const [kasaId, patch] of Object.entries(registers)) {
    bodies.push({ fiskal: { kasaId, ...patch } })
  }
  if (Object.keys(sections).length > 0) {
    bodies.push(sections)
  }
  return bodies
}

/**
 * The integration fields as a form rather than a list of dotted paths - `SAAS.md §3.86`.
 *
 * **Why a catalogue and not a component per field.** The endpoint answers 46 fields across five
 * blocks and the screen could edit ten of them, rendering the rest as monospace paths -
 * `fiskal.restauracia.cardPaymentSlot`. Legacy renders the same data as ten labelled cards. A table
 * of path/label/kind is what lets one renderer draw all of them, and - more to the point - it is
 * what lets a **ratchet** assert that every field the endpoint answers has an entry. Without that,
 * the next column added to `fiskal_settings` arrives in the response and appears nowhere, which is
 * the state this entry exists to fix.
 *
 * **The labels are legacy's own**, from its `FranchiseIntegrations` form, not invented.
 */
export type FieldKind =
  | 'text'
  | 'number'
  | 'boolean'
  | 'secret'
  | 'readonly'
  | 'languages'
  /** §3.190 - one of named values, drawn as a select. */
  | 'choice'
  /** §3.190 - a multi-line PEM block that is not secret: GPE's public key, answered whole. */
  | 'pem'
  /** §3.190 - a multi-line PEM block that is: the merchant's private key, masked like any secret. */
  | 'secret-pem'

/** A value a `choice` field offers, and what the operator reads for it. */
export interface FieldOption {
  value: string
  label: string
}

export interface IntegrationField {
  /** Full dotted path, as the payload nests it. */
  path: string
  label: string
  kind: FieldKind
  /** The values a `choice` accepts - the server refuses any other by name. */
  options?: readonly FieldOption[]
}

export interface IntegrationSection {
  key: string
  title: string
  subtitle?: string
  /** §3.190 - steps that say how to fill the card in, drawn as a numbered list. */
  guide?: readonly string[]
  fields: IntegrationField[]
}

/**
 * Whether a field is a credential the read masks - §3.190 adds the private key's PEM to the plain secrets.
 *
 * **One predicate for every place that asks**, because each of them is a place a key could leak or be erased:
 * the form must not prefill it, the save must omit it when blank, and the page must count it among the
 * `data-secret` markers `tower.spec.ts` pins to the endpoint's `{hasValue}` fields.
 */
export function isSecretKind(kind: FieldKind | null): boolean {
  return kind === 'secret' || kind === 'secret-pem'
}

/**
 * Leaf name to label and kind, per block.
 *
 * `fiskal` is keyed by leaf because the block repeats per register (`restauracia`, `rozvoz`) and the
 * two hold the same fields - the delivery register simply has fewer of them filled.
 */
/* eslint-disable no-restricted-syntax -- §3.86: these are the catalogue's labels and they are
   rendered through `<T>{field.label}</T>` by `FranchiseIntegrationsPage`, which is where the
   translation happens. Calling `tt()` here would evaluate at module load, before `setLanguage` has
   run, and freeze every label in the default language for the life of the tab. The labels are
   legacy's own, from its `FranchiseIntegrations` form. */
const FISKAL_FIELDS: readonly IntegrationField[] = [
  { path: 'isEnabled', label: 'Aktivovať fiskálnu tlač', kind: 'boolean' },
  { path: 'kasaHost', label: 'Kasa Host', kind: 'text' },
  { path: 'kasaPort', label: 'Kasa Port', kind: 'number' },
  { path: 'apiKey', label: 'API kľúč', kind: 'secret' },
  { path: 'cardPaymentSlot', label: 'Card slot', kind: 'text' },
  { path: 'implementorId', label: 'Implementor ID', kind: 'text' },
  { path: 'defaultVatRate', label: 'DPH default', kind: 'text' },
  { path: 'vatRateDineIn', label: 'DPH reštaurácia', kind: 'text' },
  { path: 'vatRateDelivery', label: 'DPH rozvoz', kind: 'text' },
  { path: 'restaurantName', label: 'Názov reštaurácie', kind: 'text' },
  { path: 'restaurantAddress', label: 'Adresa', kind: 'text' },
  { path: 'restaurantIco', label: 'IČO', kind: 'text' },
  { path: 'restaurantDic', label: 'DIČ', kind: 'text' },
]

/**
 * The card gateways a franchise may choose - `BACKEND.md` §10A.444, owner O145: *"one franchise will use tatrapay,
 * second one GP Webpay ... and later we will implement more"*. The values are `payment_settings.gateway`'s, which
 * `TenantIntegrationsService.CARD_GATEWAYS` refuses anything outside of; `CardGatewaysAgreeTest` holds the two lists
 * together.
 */
export const CARD_GATEWAY_OPTIONS: readonly FieldOption[] = [
  { value: 'tatrapay', label: 'TatraPay+ (Tatra banka)' },
  { value: 'gpwebpay', label: 'GP webpay (Slovenská sporiteľňa)' },
]

const BLOCK_FIELDS: Record<string, readonly IntegrationField[]> = {
  payments: [
    { path: 'isActive', label: 'Aktívne', kind: 'boolean' },
    { path: 'testMode', label: 'Testovací režim', kind: 'boolean' },
    // §3.190 - which gateway a new card payment goes through.
    { path: 'gateway', label: 'Brána pre platby kartou', kind: 'choice', options: CARD_GATEWAY_OPTIONS },
    { path: 'tatrapayClientId', label: 'Client ID', kind: 'text' },
    { path: 'tatrapayClientSecret', label: 'Client Secret', kind: 'secret' },
    { path: 'callbackUrl', label: 'Callback URL', kind: 'text' },
    { path: 'returnUrl', label: 'Return URL', kind: 'text' },
    { path: 'gpwebpayMerchantNumber', label: 'Číslo obchodníka', kind: 'text' },
    { path: 'gpwebpayProvider', label: 'Provider', kind: 'text' },
    { path: 'gpwebpayPrivateKey', label: 'Súkromný kľúč obchodníka (PEM)', kind: 'secret-pem' },
    { path: 'gpwebpayPrivateKeyPassword', label: 'Heslo ku kľúču', kind: 'secret' },
    { path: 'gpwebpayPublicKey', label: 'Verejný kľúč GPE (PEM)', kind: 'pem' },
    {
      path: 'gpwebpaySendCardholder',
      label: 'Posielať meno, e-mail a telefón zákazníka (PSD2)',
      kind: 'boolean',
    },
  ],
  email: [
    { path: 'isEmailEnabled', label: 'Email aktívny', kind: 'boolean' },
    { path: 'fromEmail', label: 'From Email', kind: 'text' },
    { path: 'fromName', label: 'From Name', kind: 'text' },
    { path: 'resendApiKey', label: 'Resend API kľúč', kind: 'secret' },
  ],
  sms: [
    { path: 'deliveryEnabled', label: 'SMS pri doručení', kind: 'boolean' },
    { path: 'delayEnabled', label: 'SMS pri zdržaní', kind: 'boolean' },
    // §3.86 - `deliverySenderName`, not `delaySenderName`. The read used to advertise a key the
    // write refuses, so this field was visible and unsettable.
    { path: 'deliverySenderName', label: 'Sender name', kind: 'text' },
    { path: 'bulkgateAppId', label: 'BulkGate Application ID', kind: 'text' },
    { path: 'bulkgateAppToken', label: 'BulkGate App Token', kind: 'secret' },
  ],
  /* FB-806 - **read-only, every one of them**, and not because saving is hard.
   *
   * `integrationPatches` cannot carry a `hikvision.*` key and `TenantIntegrationsService.update` has no
   * branch for one, so these were six inputs that took a value, reported a successful save and wrote
   * nothing. And `describeHikvision` **masks** `passwordHash` and `deviceToken`, over a column that
   * stores a bcrypt hash - so a box here would be wrong even if it did save.
   *
   * The terminal is configured in the manager, where `HikvisionService` owns the write and its
   * validation. A second writer in this blob would be `agos-one-guard-spelled-twice` on device
   * credentials; if the Tower is ever to set this, it is an endpoint delegating to that service. */
  hikvision: [
    { path: 'ip', label: 'IP', kind: 'readonly' },
    { path: 'port', label: 'Port', kind: 'readonly' },
    { path: 'username', label: 'Username', kind: 'readonly' },
    { path: 'passwordHash', label: 'Heslo', kind: 'readonly' },
    { path: 'deviceToken', label: 'Device token', kind: 'readonly' },
    { path: 'pollInterval', label: 'Poll interval (s)', kind: 'readonly' },
  ],
  integrations: [
    { path: 'woltApiKey', label: 'Wolt API kľúč', kind: 'secret' },
    { path: 'woltVenueId', label: 'Wolt Venue ID', kind: 'text' },
    { path: 'woltActive', label: 'Wolt aktívne', kind: 'boolean' },
    { path: 'boltApiKey', label: 'Bolt API kľúč', kind: 'secret' },
    { path: 'boltVenueId', label: 'Bolt Venue ID', kind: 'text' },
    { path: 'boltActive', label: 'Bolt aktívne', kind: 'boolean' },
    { path: 'foodoraApiKey', label: 'Foodora API kľúč', kind: 'secret' },
    { path: 'foodoraVenueId', label: 'Foodora Venue ID', kind: 'text' },
    { path: 'foodoraActive', label: 'Foodora aktívne', kind: 'boolean' },
    { path: 'googleMapsApiKey', label: 'Google Maps API kľúč', kind: 'secret' },
    { path: 'anthropicApiKey', label: 'Anthropic API kľúč', kind: 'secret' },
    {
      path: 'autoTranslateEnabled',
      label: 'Auto-translate pri každom uložení produktu (drahšie)',
      kind: 'boolean',
    },
    /* §3.113 - **a list of language codes, and legacy does have an editor for it.** The comment that
     * stood here said the opposite - *"Legacy has no editor for it either, and inventing one is a
     * decision about what a franchise may be translated into rather than a port"* - and
     * `FranchiseIntegrations.tsx` draws a grid of twelve toggles over `ALL_LANGUAGES` with a tick on
     * each active one. So it was a port that had been skipped, recorded as a decision that had been
     * taken: `agos-check-legacy-before-designing`, and `agos-a-recorded-decision-can-still-be-wrong`.
     *
     * `TenantIntegrationsService.BASIC_FIELDS` has always accepted it, which makes this the literal
     * shape of `agos-the-server-accepts-more-than-the-screen-offers`: a super-admin could not turn
     * Hungarian on for Šurany, and nothing but the screen was stopping them. */
    { path: 'autoTranslateLanguages', label: 'Jazyky auto-prekladu', kind: 'languages' },
  ],
}

const SECTION_TITLES: Record<string, { title: string; subtitle?: string }> = {
  'fiskal.restauracia': { title: 'Fiskál - Reštaurácia', subtitle: 'kasa_id = restauracia' },
  'fiskal.rozvoz': { title: 'Fiskál - Rozvoz', subtitle: 'kasa_id = rozvoz' },
  /* §3.190 - the one payments card is three. `payments` keeps the key (and `tower.spec.ts`'s marker) for the card
   * that says which gateway is used; the two gateways' own credentials have a card each. */
  payments: {
    title: 'Online platby kartou',
    subtitle:
      'Ktorou bránou zákazníci platia kartou. Zmena platí pre nové platby - otvorená platba dobehne cez bránu, ktorou začala.',
  },
  'payments.tatrapay': {
    title: 'TatraPay+ (Tatra banka)',
    subtitle:
      'QR platba pri dverách (aj tá, ktorú zákazník zvolí na webe) ide cez bránu zvolenú pre platby kartou - na GP webpay cez GP webpay, takže tieto kľúče tam pre QR netreba.',
  },
  'payments.gpwebpay': {
    title: 'GP webpay (Slovenská sporiteľňa)',
    subtitle:
      'Kľúč sa číta pri uložení - zlé heslo alebo poškodený kľúč server odmietne a povie prečo.',
  },
  email: { title: 'Email (Resend)' },
  sms: { title: 'SMS (BulkGate)' },
  /* FB-806 - the sentence is the part somebody needs, which is §3.135's finding. Without it the card
   * reads as a setting the Tower owns and merely fails to save. */
  hikvision: {
    title: 'Hikvision',
    subtitle: 'Len na čítanie - terminál sa nastavuje v manažéri franšízy (Dochádzka)',
  },
  /* §3.135 - the `integrations` blob is drawn as legacy's three cards rather than one. These titles are the
   * subsections; the blob itself no longer has a card of its own. */
  'integrations.delivery': { title: 'Rozvozné platformy (Wolt / Bolt / Foodora)' },
  'integrations.maps': {
    title: 'Google Maps',
    subtitle:
      'Geokódovanie / adresy. Prázdne = použije sa platformový kľúč. Vlastný kľúč = lepšie sledovanie spotreby na franšíze.',
  },
  'integrations.ai': {
    title: 'AI preklad (Anthropic / Claude)',
    subtitle: 'Manager UI hovorí len „AI preklad" - kľúč sa riadi odtiaľto.',
  },
}

/**
 * The integrations a brand-new franchise has none of yet - §3.136.
 *
 * **Derived from `SECTION_TITLES` rather than written out**, so a card renamed there renames the sentence the
 * creation wizard shows. A hand-written list would go quietly wrong the first time somebody renamed a card, and a
 * fifth credentialled integration would leave it quietly short.
 *
 * **The fiskal register is named plainly rather than by card**, because there are two cards for it
 * (`fiskal.restauracia` and `fiskal.rozvoz`) and a franchise created a minute ago has neither.
 */
export const SETUP_NEEDED_BLOCKS: readonly string[] = ['payments', 'email', 'sms']

export function setupNeededTitles(): readonly string[] {
  return [
    'Fiskálna pokladňa',
    ...SETUP_NEEDED_BLOCKS.map((block) => SECTION_TITLES[block]?.title ?? block),
  ]
}

/**
 * How the one `integrations` blob is split into legacy's three cards - §3.135, owner O32.
 *
 * **The server's grouping is not a product decision.** Delivery platforms, a geocoding key and a translation key
 * share one column and nothing else; legacy draws them as three sections with their own titles and their own
 * explanations, and AGOS drew them as one block of thirteen boxes.
 *
 * **Named lists rather than a prefix rule**, because a prefix rule would put a new `googleXyz` field in the maps
 * card by accident and a new `translateXyz` nowhere at all. With names, a field added to the blob and to no list
 * **fails the partition test** instead of silently vanishing from the screen - which is
 * `agos-the-server-accepts-more-than-the-screen-offers`'s mirror, four times in a week in this file.
 */
/**
 * How the GP webpay card says it is set up - §3.190, in the order an operator does it. Read off GPE's API HTTP
 * (the keys, the environments) and API WS Annex 5 (the provider code).
 */
export const GP_WEBPAY_GUIDE: readonly string[] = [
  'V GP webpay Portáli (portal.gpwebpay.com) vygenerujte kľúč obchodníka a stiahnite ho - súbor .key alebo .pem - aj s heslom, ktoré ste pri ňom zadali.',
  'Stiahnite verejný kľúč GPE (gpe.signing) pre prostredie, ktoré zodpovedá Testovaciemu režimu. GPE vydáva kľúče pre každé prostredie zvlášť: pred ostrými platbami nahrajte v portáli produkčný kľúč obchodníka, sem vložte produkčný verejný kľúč GPE a až potom vypnite Testovací režim.',
  'Číslo obchodníka je v zmluve so Slovenskou sporiteľňou. Provider 0902 je Slovenská sporiteľňa (Global Payments s.r.o. - SK).',
  'Uložte a v karte Online platby kartou zvoľte GP webpay. Hlavný administrátor franšízy overí spojenie v manažéri (Online platby - tlačidlo Otestovať spojenie) a potom jednu platbu v Testovacom režime.',
]

/**
 * How the one `payments` block is split into three cards - §3.190, the way §3.135 split `integrations`.
 *
 * **Named lists, and a field drawn only when the read answers it.** A franchise whose database has not reached
 * `V103` answers no `gateway` and no GP webpay field, and offering them there would be a save the server cannot
 * store - so the GP webpay card is simply absent until the migration runs.
 */
export const PAYMENT_SPLITS: readonly { key: string; paths: readonly string[] }[] = [
  { key: 'payments', paths: ['isActive', 'testMode', 'gateway'] },
  {
    key: 'payments.tatrapay',
    paths: ['tatrapayClientId', 'tatrapayClientSecret', 'callbackUrl', 'returnUrl'],
  },
  {
    key: 'payments.gpwebpay',
    paths: [
      'gpwebpayMerchantNumber',
      'gpwebpayProvider',
      'gpwebpayPrivateKey',
      'gpwebpayPrivateKeyPassword',
      'gpwebpayPublicKey',
      'gpwebpaySendCardholder',
    ],
  },
]

export const INTEGRATION_SPLITS: readonly { key: string; paths: readonly string[] }[] = [
  {
    key: 'integrations.delivery',
    paths: [
      'woltApiKey',
      'woltVenueId',
      'woltActive',
      'boltApiKey',
      'boltVenueId',
      'boltActive',
      'foodoraApiKey',
      'foodoraVenueId',
      'foodoraActive',
    ],
  },
  { key: 'integrations.maps', paths: ['googleMapsApiKey'] },
  {
    key: 'integrations.ai',
    paths: ['anthropicApiKey', 'autoTranslateEnabled', 'autoTranslateLanguages'],
  },
]

/* eslint-enable no-restricted-syntax */

/**
 * Every field the catalogue holds for the `integrations` blob - §3.135.
 *
 * Exported for the partition test rather than exposing `BLOCK_FIELDS` itself: what that test needs to know is
 * *which fields exist for this blob*, and a field added there without being added to a split would otherwise be
 * accepted by the server and drawn by nothing.
 */
export function integrationBlobPaths(): readonly string[] {
  return (BLOCK_FIELDS.integrations ?? []).map((field) => field.path)
}

/** Every field the catalogue holds for the `payments` block, for §3.190's partition test. */
export function paymentBlockPaths(): readonly string[] {
  return (BLOCK_FIELDS.payments ?? []).map((field) => field.path)
}

/** Every path the catalogue knows, for the ratchet and for the save. */
export function catalogueKind(path: string): FieldKind | null {
  const parts = path.split('.')
  if (parts[0] === 'fiskal' && parts.length === 3) {
    return FISKAL_FIELDS.find((field) => field.path === parts[2])?.kind ?? null
  }
  if (parts.length !== 2) return null
  const block = BLOCK_FIELDS[parts[0] ?? '']
  return block?.find((field) => field.path === parts[1])?.kind ?? null
}

/**
 * The sections to draw, in legacy's order, **built from what the response contains**.
 *
 * `hikvision` is absent on the owner's own data - `hikvision_config` has no row and the read is
 * conditional on it - and a *Hikvision* card with eight empty boxes for a franchise with no camera
 * is worse than no card. Same for a fiscal register the franchise has not configured.
 */
export function integrationSectionsOf(payload: unknown): IntegrationSection[] {
  const row = (typeof payload === 'object' && payload !== null ? payload : {}) as Record<
    string,
    unknown
  >
  const sections: IntegrationSection[] = []

  const fiskal = (typeof row.fiskal === 'object' && row.fiskal !== null ? row.fiskal : {}) as Record<
    string,
    unknown
  >
  for (const kasaId of Object.keys(fiskal)) {
    const key = `fiskal.${kasaId}`
    sections.push({
      key,
      title: SECTION_TITLES[key]?.title ?? key,
      ...(SECTION_TITLES[key]?.subtitle === undefined
        ? {}
        : { subtitle: SECTION_TITLES[key]?.subtitle }),
      fields: FISKAL_FIELDS.map((field) => ({ ...field, path: `${key}.${field.path}` })),
    })
  }

  /* FB-806 - `hikvision` is drawn and is **not** in `WRITABLE_BLOCKS`, which is the whole point of
   * listing it separately rather than widening that constant: the operator should see the terminal's
   * settings, and `integrationPatches` must go on refusing to send them. `BLOCK_FIELDS` marks each of
   * its fields `readonly`, and `EveryUnwritableBlockIsReadOnlyTest` requires that of any block here
   * which that constant does not name. */
  for (const block of [...WRITABLE_BLOCKS, 'hikvision']) {
    if (!(block in row)) continue
    const fields = BLOCK_FIELDS[block]
    if (fields === undefined) continue
    /* §3.135 - `integrations` becomes legacy's three cards. The paths are unchanged (`integrations.<field>`), so
     * the page's single save writes exactly what one card wrote. */
    if (block === 'integrations') {
      for (const split of INTEGRATION_SPLITS) {
        const chosen = split.paths
          .map((path) => fields.find((field) => field.path === path))
          .filter((field): field is (typeof fields)[number] => field !== undefined)
        if (chosen.length === 0) continue
        sections.push({
          key: split.key,
          title: SECTION_TITLES[split.key]?.title ?? split.key,
          ...(SECTION_TITLES[split.key]?.subtitle === undefined
            ? {}
            : { subtitle: SECTION_TITLES[split.key]?.subtitle }),
          fields: chosen.map((field) => ({ ...field, path: `${block}.${field.path}` })),
        })
      }
      continue
    }
    /* §3.190 - `payments` becomes three cards, each drawing only what the read answers. */
    if (block === 'payments') {
      const answered = (
        typeof row.payments === 'object' && row.payments !== null ? row.payments : {}
      ) as Record<string, unknown>
      for (const split of PAYMENT_SPLITS) {
        const chosen = split.paths
          .filter((path) => path in answered)
          .map((path) => fields.find((field) => field.path === path))
          .filter((field): field is (typeof fields)[number] => field !== undefined)
        if (chosen.length === 0) continue
        sections.push({
          key: split.key,
          title: SECTION_TITLES[split.key]?.title ?? split.key,
          ...(SECTION_TITLES[split.key]?.subtitle === undefined
            ? {}
            : { subtitle: SECTION_TITLES[split.key]?.subtitle }),
          ...(split.key === 'payments.gpwebpay' ? { guide: GP_WEBPAY_GUIDE } : {}),
          fields: chosen.map((field) => ({ ...field, path: `${block}.${field.path}` })),
        })
      }
      continue
    }
    sections.push({
      key: block,
      title: SECTION_TITLES[block]?.title ?? block,
      ...(SECTION_TITLES[block]?.subtitle === undefined
        ? {}
        : { subtitle: SECTION_TITLES[block]?.subtitle }),
      fields: fields.map((field) => ({ ...field, path: `${block}.${field.path}` })),
    })
  }

  return sections
}

/**
 * Every leaf path the payload answers, so a test can prove the catalogue covers them.
 *
 * A masked secret is `{hasValue: …}` and counts as one leaf, not as a nested object.
 */
export function answeredPaths(payload: unknown): string[] {
  const found: string[] = []
  function walk(value: unknown, path: string): void {
    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      const keys = Object.keys(value)
      if (keys.length === 1 && keys[0] === 'hasValue') {
        found.push(path)
        return
      }
      for (const [key, child] of Object.entries(value)) {
        walk(child, path === '' ? key : `${path}.${key}`)
      }
      return
    }
    if (path !== '') found.push(path)
  }
  walk(payload, '')
  return found
}

/**
 * What the form starts with: the answered value for every editable field, as a string.
 *
 * **A secret starts empty, and that is the whole reason the rest of this module exists.** The read
 * masks it, so there is nothing to prefill, and an empty box on save means *unchanged*.
 */
export function formValuesOf(payload: unknown): Record<string, string> {
  const values: Record<string, string> = {}
  for (const path of answeredPaths(payload)) {
    const kind = catalogueKind(path)
    if (kind === null || isSecretKind(kind) || kind === 'readonly') continue
    if (kind === 'languages') {
      values[path] = languageValue(languagesAt(payload, path))
      continue
    }
    values[path] = formValueText(valueAt(payload, path))
  }
  return values
}

/**
 * The selected language codes, as the form carries them - §3.113.
 *
 * **A comma-joined string, because `typed` is `Record<string, string>`.** Widening that map to hold an
 * array for one field would put a union in every reader of it; joining is one function each way and the
 * round trip is asserted rather than assumed.
 *
 * Order is the catalogue's, never the click order: two operators picking the same four languages must
 * produce the same value, or the diff against `original` reports a change that is not one.
 */
export function languageValue(codes: readonly string[]): string {
  const chosen = new Set(codes)
  return LANGUAGE_CATALOG.filter((language) => chosen.has(language.code))
    .map((language) => language.code)
    .join(',')
}

/**
 * Back to codes, dropping anything the catalogue does not offer.
 *
 * **Unknown codes are dropped rather than kept.** A stored `xx` cannot be turned off from a grid that
 * does not draw it, so keeping it would make the field impossible to clear - and the server would go on
 * asking Claude for a language nobody selected.
 */
export function languageList(value: string): string[] {
  const offered = new Set(LANGUAGE_CATALOG.map((language) => language.code))
  return value
    .split(',')
    .map((code) => code.trim())
    .filter((code) => offered.has(code as (typeof LANGUAGE_CATALOG)[number]['code']))
}

/** Whether a code is selected in a form value. */
export function languageIsOn(value: string, code: string): boolean {
  return languageList(value).includes(code)
}

/** The value with one language turned on or off - the grid's whole behaviour. */
export function languageToggled(value: string, code: string): string {
  const current = languageList(value)
  return languageValue(
    current.includes(code) ? current.filter((each) => each !== code) : [...current, code],
  )
}

function languagesAt(payload: unknown, path: string): string[] {
  let current: unknown = payload
  for (const part of path.split('.')) {
    if (typeof current !== 'object' || current === null) return []
    current = (current as Record<string, unknown>)[part]
  }
  return Array.isArray(current) ? current.map((entry) => String(entry)) : []
}

/**
 * The raw value for a form control, which is **not** `settingValueText`.
 *
 * That one is for display and answers `áno` / `nie` for a boolean - correct on a read-only row and
 * wrong in a checkbox, where the value has to round-trip back through `formPatches`. Caught by the
 * test asserting `payments.isActive` prefills as `true`, which read `áno`.
 */
function formValueText(value: string | number | boolean | null): string {
  if (value === null) return ''
  if (typeof value === 'boolean') return value ? 'true' : 'false'
  return String(value)
}

function valueAt(payload: unknown, path: string): string | number | boolean | null {
  let current: unknown = payload
  for (const part of path.split('.')) {
    if (typeof current !== 'object' || current === null) return null
    current = (current as Record<string, unknown>)[part]
  }
  if (typeof current === 'string' || typeof current === 'number' || typeof current === 'boolean') {
    return current
  }
  return null
}

/**
 * The patch bodies for a form, from what the operator actually changed.
 *
 * **Only changed fields travel.** Posting the whole form back would send 36 unchanged values on
 * every save, which is noise in the audit trail (§3.46 records *what* changed) and turns one
 * mistyped field into a rewrite of the section.
 *
 * A number is sent as a number: `kasaPort` is a smallint column and `"6091"` is not the same body.
 */
export function formPatches(
  original: Record<string, string>,
  typed: Record<string, string>,
): Record<string, unknown>[] {
  const changed: Record<string, unknown> = {}
  for (const [path, value] of Object.entries(typed)) {
    const kind = catalogueKind(path)
    if (kind === null || kind === 'readonly') continue
    if (isSecretKind(kind)) {
      // Unchanged means the operator did not retype it; empty means the same thing to the server.
      if (value !== '') changed[path] = value
      continue
    }
    if ((original[path] ?? '') === value) continue
    if (kind === 'number') {
      const parsed = Number(value)
      if (value === '' || Number.isNaN(parsed)) continue
      changed[path] = parsed
      continue
    }
    if (kind === 'boolean') {
      changed[path] = value === 'true'
      continue
    }
    if (kind === 'languages') {
      // **An array, not the joined string.** The column is a JSON list and the endpoint writes it
      // through unchanged, so sending `"sk,hu"` would store a one-element list whose single entry is
      // two languages - and `AiTranslationService` would then translate into nothing.
      changed[path] = languageList(value)
      continue
    }
    changed[path] = value
  }
  return bodiesFor(changed)
}

/** Group the changed paths into the bodies the endpoint takes, one per fiscal register. */
function bodiesFor(changed: Record<string, unknown>): Record<string, unknown>[] {
  const sections: Record<string, Record<string, unknown>> = {}
  const registers: Record<string, Record<string, unknown>> = {}

  for (const [path, value] of Object.entries(changed)) {
    const parts = path.split('.')
    if (parts[0] === 'fiskal' && parts.length === 3) {
      const kasaId = parts[1] ?? ''
      const field = parts[2] ?? ''
      if (kasaId === '' || field === '') continue
      registers[kasaId] = { ...(registers[kasaId] ?? {}), [field]: value }
      continue
    }
    if (parts.length !== 2) continue
    const block = parts[0] ?? ''
    const field = parts[1] ?? ''
    if (block === '' || field === '') continue
    sections[block] = { ...(sections[block] ?? {}), [field]: value }
  }

  const bodies: Record<string, unknown>[] = []
  for (const [kasaId, patch] of Object.entries(registers)) {
    bodies.push({ fiskal: { kasaId, ...patch } })
  }
  if (Object.keys(sections).length > 0) bodies.push(sections)
  return bodies
}
